import { ChangeDetectionStrategy, Component, forwardRef, inject, Input, OnDestroy } from '@angular/core';
import { ModalService } from '@base/services/private/modal.service';
import { MeetingOption } from '@base/enums/meet.enum';
import { ModalBodyComponent } from '@base/components/private/modals/modal-body/modal-body.component';
import { ModalFooterComponent } from '@base/components/private/modals/modal-footer/modal-footer.component';
import { ModalHeaderComponent } from '@base/components/private/modals/modal-header/modal-header.component';
import { TranslatePipe } from '@base/pipes/private/translate.pipe';
import { GlobalStateService } from '@base/services/private/global-state.service';
import { GlobalService } from '@base/services/private/global.service';

@Component({
  selector: 'app-modal-request-meet-success',
  imports: [ModalHeaderComponent, ModalBodyComponent, ModalFooterComponent, forwardRef(() => TranslatePipe)],
  providers: [forwardRef(() => TranslatePipe)],
  templateUrl: './modal-request-meet-success.component.html',
  styleUrl: './modal-request-meet-success.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalRequestMeetSuccessComponent implements OnDestroy {
  private readonly modalSrv = inject(ModalService);
  private readonly globalStateSrv = inject(GlobalStateService);
  private readonly globalSrv = inject(GlobalService);
  private readonly translatePipe = inject(TranslatePipe);

  @Input() modalId = '';
  @Input() name = '';
  @Input() peopleId = '';
  @Input() type: string | MeetingOption | null = null;
  @Input() isCloseAll = false;
  @Input() titleForMiniApp = '';

  MeetingOption = MeetingOption;

  isExternalMode = this.globalStateSrv.isExternalMode();

  get requestSentTranslationKey(): string {
    if (this.type === MeetingOption.UNSTRUCTURED) return 'YOUR_CHAT_REQUEST_HAS_BEEN_SENT';
    return this.isMeetingRequest ? 'YOUR_MEETING_REQUEST_HAS_BEEN_SENT' : 'YOUR_CONNECTION_REQUEST_HAS_BEEN_SENT';
  }

  get plannerTranslationKey(): string {
    return this.isMeetingRequest
      ? 'YOUR_MEETING_WITH_SOMEONE_WILL_BE_LISTED_ON_PLANNER'
      : 'YOUR_CONNECTION_WITH_SOMEONE_WILL_BE_LISTED_ON_PLANNER';
  }

  protected get plannerLabel(): string {
    return this.translatePipe.transform('PLANNER').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  private get isMeetingRequest(): boolean {
    return (
      this.type === MeetingOption.PHYSICAL || this.type === MeetingOption.VIRTUAL || this.type === MeetingOption.HYBRID
    );
  }

  ngOnDestroy(): void {
    this.globalSrv.destroyModalRef(this.modalId);
  }

  /**
   * Closes the modal (or all modals if `closeAll` is true) and updates the request label event.
   * @returns void
   */
  close(): void {
    this.modalSrv.close();
    this.sendPostMessage();
  }

  /**
   * Sends a post message to external targets if in full-screen mode.
   * @returns void
   */
  sendPostMessage(): void {
    // handle external mode or cancel from sense
    if (this.isExternalMode) {
      this.globalSrv.postMessageToTargets({
        is_success: true,
        message: this.translatePipe.transform(this.titleForMiniApp),
        description: this.translatePipe.transform('CLOSE_WINDOW'),
        isShowModal: true,
      });
      return;
    }
  }
}
